import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { INITIATIVES } from "@/lib/initiatives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HeartHandshake, Loader2, Sparkles, Clock, MapPin } from "lucide-react";

const AVAILABILITY = ["Weekends only", "Evenings", "Flexible / anytime", "Remote / online"];

export default function Volunteer() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", profession: "", availability: "", message: "",
  });
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (title) =>
    setInterests((s) => (s.includes(title) ? s.filter((x) => x !== title) : [...s, title]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/volunteers", { ...form, interests });
      setDone(true);
      toast.success("Welcome to the movement — we'll reach out soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="section-py">
      <div className="mx-auto max-w-7xl container-x grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">Volunteer</div>
          <h1 className="mt-3 font-serif-display text-4xl md:text-6xl text-[#0A2540] leading-[1.05]">
            Give an hour. Change a life.
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] max-w-lg">
            Time is the most valuable donation. Whether you have skills to teach,
            weekends to spend in the field, or an evening to help online — we'd love
            to have you.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {[
              { icon: Sparkles, title: "Use your skills", body: "Teach, mentor, design, code, write — for real causes." },
              { icon: Clock, title: "On your time", body: "Weekends, evenings, remote, or full-time — you decide." },
              { icon: MapPin, title: "In your city", body: "Field programmes across Maharashtra, plus online across India." },
              { icon: HeartHandshake, title: "Meet good people", body: "Join a warm community of citizens who show up." },
            ].map((c) => (
              <div key={c.title} className="rounded-2xl border border-[#0A2540]/10 bg-white p-5">
                <c.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.75} />
                <div className="mt-3 font-medium text-[#0A2540]">{c.title}</div>
                <div className="mt-1 text-sm text-[#4A5568]">{c.body}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="rounded-3xl border border-[#0A2540]/10 bg-white p-8 md:p-10">
            <div className="text-xs uppercase tracking-[0.24em] text-[#D4AF37]">Volunteer sign-up</div>
            <h2 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#0A2540]">
              Tell us about you.
            </h2>
            {done ? (
              <div className="mt-8 rounded-2xl bg-[#F9F8F6] border border-[#D4AF37]/40 p-6 text-[#0A2540]">
                <div className="font-serif-display text-xl">Welcome, UB Mitra.</div>
                <p className="mt-2 text-sm text-[#4A5568]">
                  A programme lead will reach out within 3 working days with roles that
                  match your interests.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="volunteer-form" className="mt-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full name*</Label>
                    <Input required value={form.name} onChange={set("name")} data-testid="volunteer-name" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label>Email*</Label>
                    <Input required type="email" value={form.email} onChange={set("email")} data-testid="volunteer-email" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label>Phone*</Label>
                    <Input required value={form.phone} onChange={set("phone")} data-testid="volunteer-phone" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input value={form.city} onChange={set("city")} data-testid="volunteer-city" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label>Profession / skill</Label>
                    <Input value={form.profession} onChange={set("profession")} data-testid="volunteer-profession" className="mt-2 h-11" />
                  </div>
                  <div>
                    <Label>Availability</Label>
                    <Select value={form.availability} onValueChange={(v) => setForm((f) => ({ ...f, availability: v }))}>
                      <SelectTrigger className="mt-2 h-11" data-testid="volunteer-availability">
                        <SelectValue placeholder="Select availability" />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABILITY.map((a) => (
                          <SelectItem key={a} value={a}>{a}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Which initiatives call to you?</Label>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2">
                    {INITIATIVES.map((i) => (
                      <label key={i.slug} className="flex items-start gap-3 rounded-xl border border-[#0A2540]/10 p-3 cursor-pointer hover:border-[#0A2540]/30">
                        <Checkbox
                          checked={interests.includes(i.title)}
                          onCheckedChange={() => toggle(i.title)}
                          data-testid={`volunteer-init-${i.slug}`}
                        />
                        <span className="text-sm text-[#0A2540]">{i.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Anything else you'd like us to know?</Label>
                  <Textarea rows={3} value={form.message} onChange={set("message")} data-testid="volunteer-message" className="mt-2" />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="volunteer-submit"
                  className="rounded-full bg-[#0A2540] text-white hover:bg-[#D4AF37] hover:text-[#0A2540] px-8 py-6 text-base font-medium"
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "I'm in"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
